import { z } from "zod";
import { responseSchema } from "../http.js";
import { currencySchema, priceCentsSchema } from "./product.js";
/**
 * ═══════════════════════════════════════════════════════════
 * Cobro de la franquicia digital
 *
 * El botón "Adquirir mi franquicia" de `/franquicias` pide aquí un link
 * de pago de Stripe y manda el navegador a él. Nada más: este contrato
 * cubre ABRIR el cobro, no confirmarlo. Quién pagó, cuándo, y a quién le
 * toca comisión por esa venta lo decide el WEBHOOK, que todavía no
 * existe — ver el bloque del final.
 *
 * ── El interruptor son las variables, como en el correo ────
 * `shared/mailer.ts` estableció la regla de la casa: no hay bandera de
 * "activar pagos". Hay claves de Stripe o no las hay. Por eso la
 * respuesta de éxito NO es un solo caso sino dos, y el frontend está
 * OBLIGADO por el tipo a contemplar los dos:
 *
 *   · `ready`          → hay link, redirige.
 *   · `not_configured` → todavía no hay claves; dilo con todas sus
 *                        letras en vez de fingir que se está generando.
 *
 * El tercer estado —que algo se rompió— viaja por donde viajan todos los
 * errores del API: el sobre `{ ok: false, error }`, con el código
 * `PAYMENT_PROVIDER_ERROR` para poder distinguirlo de un fallo de red.
 *
 * ── Por qué "no configurado" es un ÉXITO y no un 503 ───────
 * Porque no es una falla: es un hecho conocido, estable y que el servidor
 * puede responder al instante y con certeza. Si viajara como error, en el
 * frontend se mezclaría con "no hay internet" y con "Stripe está caído",
 * y las tres cosas acabarían enseñando el mismo aviso genérico — cuando
 * la única de las tres que se arregla pegando dos líneas en un `.env` es
 * esta. Como dato, el tipo obliga a tratarla aparte.
 * ═══════════════════════════════════════════════════════════
 */
// ═══════════════════════════════════════════════════════════
// El precio — el del servidor, que es el único que cobra
// ═══════════════════════════════════════════════════════════
/**
 * Cuánto cuesta la franquicia, en CENTAVOS ENTEROS, por moneda.
 *
 * ⚠️ ESTE ES EL ÚNICO LUGAR DONDE VIVE EL PRECIO. Está en el contrato
 * compartido —y no en el contenido de la landing ni en una constante del
 * backend— por una razón que no es de orden sino de dinero:
 *
 *   · El servidor cobra ESTE número.
 *   · La página enseña ESTE número.
 *
 * Siendo la misma constante, no pueden separarse. Con dos copias, el día
 * que una suba y la otra no, la landing anunciaría un precio y Stripe
 * cobraría otro — y quien lo note primero será quien pague de más.
 *
 * El importe NUNCA llega del cliente. `franchiseCheckoutRequestSchema` no
 * tiene campo de monto a propósito: si el navegador pudiera decir cuánto
 * cuesta una franquicia, alguien compraría la suya por un peso.
 *
 * Los valores son los de producción: 6,000 MXN y 350 USD al año.
 */
export const FRANCHISE_PRICE_CENTS = Object.freeze({
    MXN: 6_000_00,
    USD: 350_00,
});
/**
 * El nombre del cargo en el recibo de Stripe. No se pinta en ninguna
 * pantalla: es lo que verá quien mire su estado de cuenta.
 */
export const FRANCHISE_PRODUCT_NAME = "Franquicia Digital · It's The Best (1 año)";
// ═══════════════════════════════════════════════════════════
// Las DOS cuentas de Stripe
// ═══════════════════════════════════════════════════════════
/**
 * El negocio cobra por DOS cuentas de Stripe distintas, y la moneda
 * decide cuál:
 *
 *   · MXN → cuenta de México         (`MX`)
 *   · USD → cuenta de Estados Unidos (`US`)
 *
 * Es el mismo ruteo que produccion documenta en `lib/stripeAccounts.ts`.
 * Está en el contrato desde el primer día —y no como un "ya lo
 * partiremos"— porque partir una sola cuenta en dos después obliga a
 * rehacer el endpoint entero: cambian las variables de entorno, cambia
 * qué llave firma la llamada, cambia qué secreto valida el webhook y
 * cambia a qué cuenta hay que ir a buscar un pago cuando alguien reclama.
 *
 * No hay cuenta "por defecto" ni respaldo de una moneda a la otra. Un
 * respaldo sonaría prudente y sería lo contrario: cobrarle en pesos a la
 * cuenta de Estados Unidos deja el dinero en el país equivocado, con la
 * conversión equivocada, y nadie se entera hasta que hay que devolverlo.
 * Si falta la llave de una moneda, esa moneda responde `not_configured`
 * y la otra sigue cobrando.
 */
export const STRIPE_ACCOUNTS = ["MX", "US"];
export const stripeAccountSchema = z.enum(STRIPE_ACCOUNTS);
/** Qué cuenta cobra una moneda. Total y sin excepciones. */
export const STRIPE_ACCOUNT_BY_CURRENCY = Object.freeze({
    MXN: "MX",
    USD: "US",
});
// ═══════════════════════════════════════════════════════════
// La petición
// ═══════════════════════════════════════════════════════════
/**
 * Lo único que el navegador puede decir es EN QUÉ MONEDA quiere pagar —
 * la del selector de la barra superior—, y eso porque de ahí sale además
 * qué cuenta cobra.
 *
 * Todo lo demás lo pone el servidor: el importe, el nombre del cargo, a
 * dónde vuelve el navegador al terminar y qué metadatos lleva la sesión.
 * Lo que el cliente no manda, el cliente no puede falsificar.
 */
export const franchiseCheckoutRequestSchema = z.object({
    currency: currencySchema,
});
/**
 * ═══════════════════════════════════════════════════════════════════
 * Comprar UN PAQUETE suelto, sin franquicia (Anahí, 29-ago).
 *
 * En `/franquicias/adquirir` conviven las dos salidas: a la izquierda la
 * franquicia —con lo que ese paquete costaría siendo franquiciatario— y
 * a la derecha el paquete solo, "por si no quiere la franquicia".
 *
 * ── Viaja el ID, NUNCA el precio ─────────────────────────────────────
 * Misma regla que la franquicia, y por el mismo motivo: aquí el importe
 * no es una constante del contrato sino una fila de la base, así que lo
 * que llega es CUÁL paquete y el servidor va a buscar cuánto cuesta. Un
 * `amountCents` en este esquema sería un formulario de descuentos.
 * ═══════════════════════════════════════════════════════════════════
 */
export const packageCheckoutRequestSchema = z.object({
    currency: currencySchema,
    /** El id del paquete en el catálogo. El precio lo pone el servidor. */
    packageId: z.string().trim().min(1),
});
// ═══════════════════════════════════════════════════════════
// La respuesta
// ═══════════════════════════════════════════════════════════
/** Hay link. El frontend redirige a `url` y ya no vuelve por aquí. */
export const franchiseCheckoutReadySchema = z.object({
    status: z.literal("ready"),
    /**
     * La URL alojada de Stripe (`https://checkout.stripe.com/…`). Se valida
     * como URL en el frontend igual que aquí: es un destino al que se manda
     * el navegador, así que no se acepta lo que sea que haya respondido el
     * proveedor sin mirarlo.
     */
    url: z.url(),
    /** La moneda que se pidió, devuelta para que el frontend la confirme. */
    currency: currencySchema,
    /**
     * Cuánto se va a cobrar, en centavos. Viaja de vuelta A PROPÓSITO
     * aunque el frontend ya lo sepa: es la forma de que la pantalla pueda
     * comprobar que el servidor cobra lo que ella anunció, y de que una
     * prueba pueda afirmarlo sin abrir Stripe.
     */
    amountCents: priceCentsSchema,
    /** Qué cuenta va a cobrar. Para el log y para el soporte. */
    account: stripeAccountSchema,
});
/**
 * Todavía no hay claves de Stripe para esa moneda.
 *
 * No lleva el nombre de las variables que faltan: el endpoint es PÚBLICO
 * y la configuración interna del servidor no es asunto de quien mira la
 * landing. Cuál falta se escribe en el log del servidor, que es donde
 * sirve para arreglarlo.
 */
export const franchiseCheckoutNotConfiguredSchema = z.object({
    status: z.literal("not_configured"),
    currency: currencySchema,
});
export const franchiseCheckoutSchema = z.discriminatedUnion("status", [
    franchiseCheckoutReadySchema,
    franchiseCheckoutNotConfiguredSchema,
]);
export const franchiseCheckoutResponseSchema = responseSchema(franchiseCheckoutSchema);
/*
 * El paquete responde con la MISMA forma que la franquicia: o hay link,
 * o falta la llave de esa moneda. Un segundo esquema idéntico solo sería
 * un sitio más donde olvidarse de añadir el tercer final el día que lo
 * haya.
 */
export const packageCheckoutSchema = franchiseCheckoutSchema;
export const packageCheckoutResponseSchema = franchiseCheckoutResponseSchema;
// ═══════════════════════════════════════════════════════════
// Rutas
// ═══════════════════════════════════════════════════════════
export const CHECKOUT_ROUTES = {
    /**
     * Público y sin sesión, como la landing desde la que se llama. Con un
     * límite por IP MUY estrecho en el servidor: cada llamada abre una
     * sesión en Stripe, o sea que cuesta dinero y llena la cuenta de
     * sesiones muertas.
     *
     * Si hay sesión iniciada, el token viaja igual y el servidor la
     * aprovecha para dejar escrito QUIÉN está comprando (ver abajo).
     */
    franchise: "/api/checkout/franchise",
    /**
     * Un paquete suelto. Mismo límite por IP y misma regla del precio: lo
     * pone el servidor. Exige sesión —a diferencia de la franquicia—
     * porque un paquete se le acredita a alguien: sin dueño no hay puntos
     * de volumen que repartir ni recompra que arrancar.
     */
    package: "/api/checkout/paquete",
    /**
     * ⚠️ TODAVÍA NO EXISTE. Reservada aquí para que nadie la invente en
     * otro sitio. Ver el bloque de abajo.
     */
    // stripeWebhook: "/api/checkout/stripe/webhook",
};
/**
 * ═══════════════════════════════════════════════════════════
 * EL SIGUIENTE PASO: el webhook — lo que este archivo NO hace
 *
 * Hoy este contrato termina donde empieza Stripe. Se abre la sesión, se
 * manda el navegador y se acabó. Que alguien haya PAGADO no lo sabe
 * todavía nadie de este lado, y por eso hoy no se activa ninguna
 * franquicia ni se paga ninguna comisión.
 *
 * Lo que falta es UN endpoint más, `POST /api/checkout/stripe/webhook`,
 * con estas piezas:
 *
 *  1. **Dos secretos de firma, uno por cuenta**
 *     (`STRIPE_WEBHOOK_SECRET_MX` y `STRIPE_WEBHOOK_SECRET_US`). Sin
 *     verificar la firma, ese endpoint es un botón público para regalar
 *     franquicias: cualquiera puede mandar un JSON que diga "pagado".
 *     Cada cuenta firma con SU secreto; probar los dos contra cada
 *     petición es lo que evita tener que adivinar de cuál vino.
 *
 *  2. **El cuerpo CRUDO.** La firma se calcula sobre los bytes tal como
 *     llegaron. `express.json()` los reemplaza por un objeto y la firma
 *     deja de cuadrar, así que esa ruta necesita `express.raw()` montado
 *     ANTES del parser general, en `app.ts`.
 *
 *  3. **Idempotencia.** Stripe reintenta el mismo evento hasta que
 *     responden 200. Sin una colección que recuerde qué `event.id` ya se
 *     procesó, un reintento paga la comisión dos veces.
 *
 *  4. **A quién se le acredita.** Es el hueco más grande de hoy: la
 *     landing es pública y se puede pedir el link sin haber entrado. El
 *     servicio ya escribe `client_reference_id` y `metadata.userId`
 *     CUANDO hay sesión; cuando no la hay, el pago llega sin dueño y hay
 *     que casarlo a mano por el correo del recibo. Mientras eso siga
 *     así, el bono no se puede pagar solo.
 *
 *  5. **Y entonces marcar su franquicia como activa.** Con el pago
 *     confirmado y el comprador identificado. Aquí NO se paga ninguna
 *     comisión: el bono directo de franquicia (30 · 10 · 9 · 5 · 2) se
 *     quitó del plan el 14-sep-2026 por decisión de Anahí. Lo que gana
 *     el franquiciatario es el descuento en sus compras
 *     (`FRANCHISEE_DISCOUNT_PERCENT`, `contracts/pricing.ts`).
 * ═══════════════════════════════════════════════════════════
 */
//# sourceMappingURL=checkout.js.map